import { useState, useEffect, useRef } from 'react';
import axiosInstance from '@/utils/axios';
import { Input } from '@/components/ui/input';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';

export default function InstructionsAutocomplete({ 
  value, 
  onChange, 
  placeholder, 
  className, 
  dataTestId, 
  medicationName 
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Fetch instruction suggestions when medication name changes
  useEffect(() => {
    if (medicationName && medicationName.length > 2) {
      fetchInstructionSuggestions();
    } else {
      setSuggestions([]);
    }
  }, [medicationName]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchInstructionSuggestions = async () => {
    try {
      const response = await axiosInstance.get(`/medications/instructions-suggestions?medication_name=${encodeURIComponent(medicationName)}`);
      setSuggestions(response.data.instructions || []);
    } catch (error) {
      console.error('Erro ao buscar sugestões de instruções:', error);
      setSuggestions([]);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value.toUpperCase();
    setInputValue(val);
    onChange(val);

    // Show suggestions if we have them and user is typing
    if (suggestions.length > 0 && val.length >= 2) {
      const filtered = suggestions.filter(instruction =>
        instruction.toUpperCase().includes(val)
      );
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (instruction) => {
    setInputValue(instruction);
    onChange(instruction);
    setShowSuggestions(false);
  };

  const getFilteredSuggestions = () => {
    if (!inputValue || inputValue.length < 2) return suggestions;
    return suggestions.filter(instruction =>
      instruction.toUpperCase().includes(inputValue.toUpperCase())
    );
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        data-testid={dataTestId}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if (suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      
      {showSuggestions && getFilteredSuggestions().length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
          <Command>
            <CommandList>
              <CommandGroup>
                {getFilteredSuggestions().slice(0, 6).map((instruction, index) => (
                  <CommandItem
                    key={index}
                    onSelect={() => handleSelectSuggestion(instruction)}
                    className="cursor-pointer hover:bg-gray-100 px-3 py-2 text-sm"
                  >
                    <div className="truncate max-w-full">
                      {instruction}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}